// "Where's Waldo" — next day the signed-in pilot shares a port with one or more
// other pilots (Paul's request, 2026-07-17). "In port together" = both have the
// same port on the same dispatch day, regardless of duty status (assigned,
// travel, or off). A day OFF counts as being at the pilot's home dispatch port
// (Paul 2026-08-02: "when can we hang out when we're both off dispatch and
// home?"), so two Juneau pilots' shared off days now match too.
// Pure helpers so the matching is unit-testable.
//
// Beyond same-port days, two pilots can be together while TRAVELING (same
// charter leg, decoded from the lowercase travel tokens in raw_cell) or at the
// same HOTEL overnight (travel_reservations) — Jill 2026-08-17: Lewis and
// Russell shared the KTN→Kake charter and the Kake hotel and Waldo showed
// nothing, because travel-day cells usually carry no port_codes.

import { parseCellTravel } from "./travelCheck.js";
import { computeHotelOvernight } from "./hotelOvernight.js";

const iso10 = (v) => (v || "").slice(0, 10);

// The sheet writes Juneau as JUN (mostly) and JNU (sometimes), and
// pilots_info.dispatch_port says JNU. Every port that enters a match goes
// through here so the three spellings meet as one place (Jill 2026-09-07:
// two Juneau pilots both off at home never matched when the sheet said JUN).
const PORT_ALIASES = { JNU: "JUN" };
export function normPort(p) {
  const u = String(p || "").toUpperCase().trim();
  return PORT_ALIASES[u] || u;
}

// A dispatch cell's port_codes is a Postgres text[] (["END","JUN"]) over the
// REST API, but tolerate a plain string ("JNU/KTN") too.
export function parsePorts(portCodes) {
  if (Array.isArray(portCodes)) return portCodes.map(normPort).filter(Boolean);
  return (portCodes || "").toUpperCase().split(/[^A-Z]+/).map(normPort).filter(Boolean);
}

// rows: [{ duty_date, port_codes, cell_status, vessel_code }] → per-day status:
// { [date]: { ports:Set, status, vessel } }. An ASSIGNED row wins the day's
// status/vessel over an off/travel row (a pilot occasionally has both).
export function dayInfoFromRows(rows) {
  const m = {};
  for (const r of rows || []) {
    const d = iso10(r.duty_date);
    if (!d) continue;
    if (!m[d]) m[d] = { ports: new Set(), status: r.cell_status || "", vessel: r.vessel_code || "" };
    const cur = m[d];
    for (const p of parsePorts(r.port_codes)) cur.ports.add(p);
    if (r.cell_status === "ASSIGNED") { cur.status = "ASSIGNED"; if (r.vessel_code) cur.vessel = r.vessel_code; }
    else if (!cur.status) cur.status = r.cell_status || "";
  }
  return m;
}

// Plain-language status for one day's info (ship code, traveling, or home off).
export function statusLabel(info) {
  if (!info) return "";
  if (info.status === "ASSIGNED") return info.vessel ? `on ${info.vessel}` : "on a ship";
  if (info.status === "TRAVEL") return "traveling";
  if (info.status === "YACHT") return "on a yacht";
  if (info.status === "OFF_PAID" || info.status === "OFF_UNPAID") return "home, off duty";
  if (info.status === "ONCALL") return "home, on dispatch";
  return "";
}

// Winter duty weeks (Jill 2026-09-11): a pilot on dispatch is home, ready to be
// called out, so their on-dispatch days count as home-port days. Days the winter
// roster has them OFF dispatch stay unknowable, the same as a day with no sheet
// row: they could be anywhere. Only fills dates the sheet says nothing about.
export function addOnCallDays(dayInfo, onCallDates) {
  const m = { ...(dayInfo || {}) };
  for (const d of onCallDates || []) {
    if (!m[d]) m[d] = { ports: new Set(), status: "ONCALL", vessel: "" };
  }
  return m;
}

// { [date]: {ports:Set,...} } → { [date]: Set(ports) } for the matchers below.
// homePort: the pilot's dispatch port (KTN/JNU). On an OFF day the pilot is
// (presumed) home, so their home port counts as where they are — that's what
// lets "both home, off duty" days match. Days with no row at all stay
// unknowable on purpose: absent from the sheet is not the same as off.
// travelMap (optional, from travelMapFromRows): a travel overnight — a layover
// ("k>") or an overnight run ("cTk>") — puts the pilot in that place for the
// night, so it counts as a port for the day even when port_codes is empty.
export function portMapFromDayInfo(dayInfo, homePort, travelMap) {
  const m = {};
  const home = normPort(homePort);
  for (const [d, i] of Object.entries(dayInfo || {})) {
    const set = new Set([...i.ports].map(normPort));
    if (home && (i.status === "OFF_PAID" || i.status === "OFF_UNPAID" || i.status === "ONCALL")) set.add(home);
    const overnightAt = normPort(travelMap?.[d]?.overnightAt);
    if (overnightAt) set.add(overnightAt);
    m[d] = set;
  }
  return m;
}

// rows: [{ duty_date, raw_cell }] → { [date]: { legs:[{from,to}], overnightAt } }
// from the lowercase travel tokens in the dispatch cells ("cTk" = KTN→Kake).
// One parse per distinct raw_cell per day (handoff cells emit two rows).
export function travelMapFromRows(rows) {
  const cellsByDate = {};
  for (const r of rows || []) {
    const d = iso10(r.duty_date);
    if (!d) continue;
    if (!cellsByDate[d]) cellsByDate[d] = new Set();
    cellsByDate[d].add(r.raw_cell || "");
  }
  const m = {};
  for (const [d, cells] of Object.entries(cellsByDate)) {
    const legs = [];
    let overnightAt = null;
    for (const cell of cells) {
      const f = parseCellTravel(cell);
      for (const run of f.runs) {
        if (run.from || run.to) legs.push({ from: run.from, to: run.to });
        if (run.overnight && run.to) overnightAt = run.to;
      }
      if (f.layoverAt) overnightAt = f.layoverAt;
    }
    if (legs.length || overnightAt) m[d] = { legs, overnightAt };
  }
  return m;
}

// Two decoded legs are the same run when every end named on BOTH sides agrees
// and at least one concrete end is shared — so "cTk" matches "cT" (the office
// sometimes omits the destination on one pilot's cell) but never "jTs".
const sameLeg = (a, b) =>
  ((!a.from || !b.from || a.from === b.from) && (!a.to || !b.to || a.to === b.to)) &&
  ((a.from && a.from === b.from) || (a.to && a.to === b.to));
// prefer whichever side names more ends, so the display shows the full route
const fullerLeg = (a, b) => ((b.from ? 1 : 0) + (b.to ? 1 : 0) > (a.from ? 1 : 0) + (a.to ? 1 : 0) ? b : a);

// travelMaps: [mine, ...theirs] from travelMapFromRows. EVERY date on/after
// fromDate where all of them share a travel leg (same charter/ferry run).
export function sharedTravelDates(travelMaps, fromDate) {
  const [mine, ...others] = travelMaps || [];
  if (!mine || !others.length) return [];
  const out = [];
  for (const d of Object.keys(mine).filter((x) => x >= fromDate).sort()) {
    let legs = mine[d].legs;
    for (const tm of others) {
      if (!tm[d]) { legs = []; break; }
      legs = legs
        .map((la) => {
          const match = tm[d].legs.find((lb) => sameLeg(la, lb));
          return match ? fullerLeg(la, match) : null;
        })
        .filter(Boolean);
      if (!legs.length) break;
    }
    if (legs.length) {
      const seen = new Set();
      const uniq = legs.filter((l) => {
        const k = `${l.from || "?"}>${l.to || "?"}`;
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });
      out.push({ date: d, legs: uniq });
    }
  }
  return out;
}

// Which hotel is everyone at each night — computeHotelOvernight per date, only
// dates that put anyone at a hotel kept. travelRows: travel_reservations
// window (a couple weeks back through the sheet end, not cancelled);
// assignedRows: ASSIGNED dispatch rows (they close out open-ended stays).
export function hotelNightsForDates(travelRows, assignedRows, dates) {
  const m = {};
  for (const d of dates || []) {
    const groups = computeHotelOvernight(travelRows, assignedRows, d);
    if (groups.length) m[d] = groups;
  }
  return m;
}

// EVERY date on/after fromDate where one hotel's overnight list satisfies
// every test in labelTests (one test per pilot, e.g. travelLabelIsMine bound
// to that pilot). → [{ date, hotels: [codes] }]
export function sharedHotelDates(nightsByDate, labelTests, fromDate) {
  const out = [];
  if (!labelTests?.length) return out;
  for (const d of Object.keys(nightsByDate || {}).filter((x) => x >= fromDate).sort()) {
    const hotels = nightsByDate[d]
      .filter((g) => labelTests.every((test) => g.list.some((x) => test(x.label))))
      .map((g) => g.hotel);
    if (hotels.length) out.push({ date: d, hotels: hotels.sort() });
  }
  return out;
}

// Fold port hits ({date,ports}), travel hits ({date,legs}) and hotel hits
// ({date,hotels}) into one line per day for the UI.
export function mergeHits(portHits, travelHits, hotelHits) {
  const by = {};
  const at = (d) => by[d] || (by[d] = { date: d, ports: [], legs: [], hotels: [] });
  for (const h of portHits || []) at(h.date).ports = h.ports;
  for (const h of travelHits || []) at(h.date).legs = h.legs;
  for (const h of hotelHits || []) at(h.date).hotels = h.hotels;
  return Object.values(by).sort((a, b) => (a.date < b.date ? -1 : 1));
}

// rows: [{ duty_date, port_codes }] → { [date]: Set(ports) } (union per day).
export function portMapFromRows(rows) {
  const m = {};
  for (const r of rows || []) {
    const d = iso10(r.duty_date);
    if (!d) continue;
    const set = m[d] || (m[d] = new Set());
    for (const p of parsePorts(r.port_codes)) set.add(p);
  }
  return m;
}

// EVERY date on/after fromDate where mine and theirs share a port, in order
// (Paul 2026-07-22: all upcoming shared days, not just the next one).
export function sharedDates(mineMap, theirMap, fromDate) {
  const out = [];
  const dates = Object.keys(theirMap).filter((d) => d >= fromDate).sort();
  for (const d of dates) {
    if (!mineMap[d]) continue;
    const shared = [...theirMap[d]].filter((p) => mineMap[d].has(p));
    if (shared.length) out.push({ date: d, ports: shared.sort() });
  }
  return out;
}

// EVERY date where mine AND every one of theirMaps share a common port.
export function allTogetherDates(mineMap, theirMaps, fromDate) {
  const out = [];
  const dates = Object.keys(mineMap).filter((d) => d >= fromDate).sort();
  for (const d of dates) {
    let common = [...(mineMap[d] || [])];
    for (const tm of theirMaps) {
      if (!tm[d]) { common = []; break; }
      common = common.filter((p) => tm[d].has(p));
      if (!common.length) break;
    }
    if (common.length) out.push({ date: d, ports: common.sort() });
  }
  return out;
}

// Earliest matches — kept as thin wrappers over the full lists.
export function nextSharedDate(mineMap, theirMap, fromDate) {
  return sharedDates(mineMap, theirMap, fromDate)[0] || null;
}
export function nextAllTogether(mineMap, theirMaps, fromDate) {
  return allTogetherDates(mineMap, theirMaps, fromDate)[0] || null;
}
